/**
 * Whether this browser can perform a WebAuthn ceremony at all (SYS-8).
 *
 * **A module of its own, holding one line, and the reason is the barrel.**
 * `passkey.ts` already answers a capability question — `platformAuthenticatorAvailable`
 * — but it imports `@simplewebauthn/browser` and is therefore reachable only
 * through `authDriver`'s dynamic `import()`. The sign-in landing page asks *this*
 * question on every anonymous load, before anybody has clicked anything, and
 * routing it through the driver would pull that library into the one bundle
 * ADR-0050 is proudest of: a visitor who never signs in downloads no
 * authentication library at all. So it lives where it can be exported
 * statically, imports nothing, and costs nothing to ask.
 *
 * **It is not `platformAuthenticatorAvailable` with a shorter name**, and
 * conflating the two is the bug this exists to end. That function answers "is
 * there a Windows Hello or a Touch ID here", and it also answers `false` when
 * there is no `PublicKeyCredential` at all — one value for two facts. Signing in
 * with a credential you already hold needs neither a platform authenticator nor
 * a built-in one: a security key works, and so does a phone over the hybrid
 * transport. A desktop with no fingerprint reader answers `false` there and can
 * still complete an assertion perfectly well, so gating the passkey button on it
 * would hide the control from exactly the machines that need it most.
 *
 * The division of labour, therefore:
 *
 * - **this** decides whether the passkey path is offered at all;
 * - **`platformAuthenticatorAvailable`** decides whether *enrolling* is offered,
 *   because the landing page enrols in the own-device shape and ADR-0046 §3 has
 *   the server enforce that claim against the attestation.
 *
 * Synchronous, unlike its neighbour: this is a `typeof` check and not a promise
 * anybody should have to await. `isUserVerifyingPlatformAuthenticatorAvailable`
 * genuinely asks the platform, which is why that one is not.
 */
export function webAuthnSupported(): boolean {
  return typeof window.PublicKeyCredential === 'function';
}
